// A place's season follows the trip (docs/PLACES.md): a stage's calendar season comes from the
// middle of its dates and its hemisphere, and is then mapped onto the seasons the place offers.
// World → Season (a grown-up's manual pick) overrides it for the session (stage/sets/mapSet).
// Pure and three-free: App works out each trip place's dates, the Map set picks the season.

/** The four calendar seasons. */
export type CalSeason = 'winter' | 'spring' | 'summer' | 'autumn'

export type Hemisphere = 'N' | 'S'

/** A season a place offers (placekit SeasonOpt): its id, and the calendar seasons it stands for. */
export interface OfferedSeason {
  id: string
  /** Default: just its own id, when that is a calendar season. */
  covers?: readonly CalSeason[]
}

/** What the trip says about a place: the middle of its stage's dates, and which side of the equator. */
export interface TripSeason {
  /** ISO date (yyyy-mm-dd). */
  date: string
  /** The stage stands south of the equator (its anchor's latitude). */
  south?: boolean
}

const CAL: readonly CalSeason[] = ['winter', 'spring', 'summer', 'autumn']
const isCal = (s: string): s is CalSeason => (CAL as readonly string[]).includes(s)

/**
 * The calendar season of a month (1–12). Northern hemisphere: Dec–Feb winter, Mar–May spring,
 * Jun–Aug summer, Sep–Nov autumn; the southern hemisphere is six months on.
 */
export function seasonOfMonth(month: number, hemisphere: Hemisphere = 'N'): CalSeason {
  const m = (((month - 1 + (hemisphere === 'S' ? 6 : 0)) % 12) + 12) % 12 // 0 = Jan (north)
  return CAL[Math.floor(((m + 1) % 12) / 3)]!
}

const dayMs = 86_400_000
const toDay = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / dayMs
const fromDay = (d: number) => new Date(d * dayMs).toISOString().slice(0, 10)

/** The middle day of a stage's dates (ISO; the earlier of two middle days). */
export function midDate(start: string, end: string): string {
  const a = toDay(start)
  const b = toDay(end < start ? start : end)
  return fromDay(a + Math.floor((b - a) / 2))
}

/** The calendar season on an ISO date. */
export function seasonOfDate(iso: string, hemisphere: Hemisphere = 'N'): CalSeason {
  return seasonOfMonth(+iso.slice(5, 7), hemisphere)
}

/**
 * When a place doesn't offer the calendar season: spring and autumn look most like summer (green,
 * no snow). Winter and summer have no stand-in: the place's default season then.
 */
export const SEASON_FALLBACK: Readonly<Record<CalSeason, readonly CalSeason[]>> = {
  winter: ['winter'],
  spring: ['spring', 'summer'],
  summer: ['summer'],
  autumn: ['autumn', 'summer'],
}

const covers = (s: OfferedSeason): readonly CalSeason[] => s.covers ?? (isCal(s.id) ? [s.id] : [])

/**
 * The place's season for a calendar season: the first offered season that covers it (its own
 * `covers` list, else its id), trying the fallbacks in order. Null: none fits (the place's default).
 */
export function pickSeason(want: CalSeason, offered: readonly OfferedSeason[]): string | null {
  for (const cal of SEASON_FALLBACK[want]) {
    const hit = offered.find((s) => covers(s).includes(cal))
    if (hit) return hit.id
  }
  return null
}

/**
 * The season a place shows: a manual pick (World → Season) when the place offers it, else the
 * trip's season for it, else null (its default: the first). `hemisphere` is the place's own when
 * it says so; otherwise the trip's stage decides (`south`).
 */
export function placeSeason(
  offered: readonly OfferedSeason[] | null,
  manual: string | null,
  trip: TripSeason | undefined,
  hemisphere?: Hemisphere,
): string | null {
  if (!offered?.length) return null
  if (manual && offered.some((s) => s.id === manual)) return manual
  if (!trip) return null
  return pickSeason(seasonOfDate(trip.date, hemisphere ?? (trip.south ? 'S' : 'N')), offered)
}

interface DatedStage {
  spot: string
  startDate: string
  endDate: string
  anchor?: readonly [number, number] | readonly number[]
}

/**
 * Each trip place's season input: the visit that matters now (the one under way or next on
 * `today`, else the last), the middle of its dates, and its hemisphere. `placeId` maps a stage's
 * spot to today's place id.
 */
export function tripSeasons(
  stages: readonly DatedStage[],
  today: string,
  placeId: (spot: string) => string = (s) => s,
): Record<string, TripSeason> {
  const out: Record<string, TripSeason> = {}
  const chosen = new Map<string, DatedStage>()
  for (const s of stages) {
    const id = placeId(s.spot)
    const had = chosen.get(id)
    // Keep the first visit not yet over; a place visited only in the past keeps its last visit.
    if (!had || (had.endDate < today && (s.endDate >= today || s.startDate > had.startDate))) chosen.set(id, s)
  }
  for (const [id, s] of chosen) {
    const lat = s.anchor?.[0]
    out[id] = { date: midDate(s.startDate, s.endDate), ...(typeof lat === 'number' && lat < 0 ? { south: true } : {}) }
  }
  return out
}
